import React, { useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faInbox,
  faEye,
  faUserTie,
  faClock,
  faMobileAlt,
  faArrowRight,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { formatDurationMinutes } from '../../TicketsView/utils/ticketFormatters';
import { formatWhatsAppText } from '../../TicketDetailView/utils';

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'WA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const getStatusBadge = (statusId) => {
  switch (Number(statusId)) {
    case 1:
      return {
        label: 'Pendiente',
        bg: '#fef3c7',
        color: '#92400e',
        darkBg: 'rgba(245, 158, 11, 0.2)',
        darkColor: '#fcd34d',
        accentColor: '#f59e0b'
      };
    case 3:
      return {
        label: 'En Progreso',
        bg: '#dbeafe',
        color: '#1e40af',
        darkBg: 'rgba(59, 130, 246, 0.2)',
        darkColor: '#93c5fd',
        accentColor: '#0ea5e9'
      };
    case 2:
      return {
        label: 'Resuelto',
        bg: '#d1fae5',
        color: '#065f46',
        darkBg: 'rgba(16, 185, 129, 0.2)',
        darkColor: '#6ee7b7',
        accentColor: '#10b981'
      };
    default:
      return {
        label: 'Desconocido',
        bg: '#f3f4f6',
        color: '#374151',
        darkBg: 'rgba(107, 114, 128, 0.2)',
        darkColor: '#d1d5db',
        accentColor: '#94a3b8'
      };
  }
};

const ReportTable = ({
  tickets = [],
  isLoading = false,
  totalTicketsCount = 0,
  onSelectTicket
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);

  return (
    <div
      className="rounded-3 border overflow-hidden shadow-none mb-3"
      style={{
        backgroundColor: isDark ? '#121e2d' : '#ffffff',
        borderColor: isDark ? '#233142' : '#e2e8f0'
      }}
    >
      {isLoading && totalTicketsCount === 0 ? (
        <div className="text-center py-5">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-2" />
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Cargando reporte de tickets...
          </p>
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-5">
          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: '56px',
              height: '56px',
              backgroundColor: isDark ? '#1e293b' : '#f1f5f9'
            }}
          >
            <FontAwesomeIcon icon={faInbox} style={{ fontSize: '1.6rem', color: isDark ? '#64748b' : '#94a3b8' }} />
          </div>
          <h6 className="fw-bold mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
            No se encontraron tickets en el reporte
          </h6>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Intenta ampliando el rango de fechas o cambiando los filtros seleccionados.
          </p>
        </div>
      ) : (
        <>
          {/* Vista Escritorio: Tabla completa */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-hover align-middle mb-0" style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
              <thead
                style={{
                  backgroundColor: isDark ? '#0b1727' : '#f8fafc',
                  borderBottom: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
                  fontSize: '0.78rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: isDark ? '#94a3b8' : '#64748b'
                }}
              >
                <tr>
                  <th className="py-3 px-3">Ticket</th>
                  <th className="py-3 px-3">Fecha</th>
                  <th className="py-3 px-3">Cliente</th>
                  <th className="py-3 px-3">Asunto / Mensaje</th>
                  <th className="py-3 px-3">Asesor</th>
                  <th className="py-3 px-3 text-center">Estatus</th>
                  <th className="py-3 px-3 text-center">Tiempo 1ra Respuesta</th>
                  <th className="py-3 px-3 text-end">Acción</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(ticket => {
                  const badge = getStatusBadge(ticket.iEstadoTicket);
                  const createdDate = ticket.dCreatedDate
                    ? new Date(ticket.dCreatedDate).toLocaleDateString('es-MX', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                    : '-';

                  return (
                    <tr
                      key={ticket.idTicket}
                      style={{
                        borderColor: isDark ? '#233142' : '#f1f5f9'
                      }}
                    >
                      <td className="px-3 py-3" style={{ fontSize: '0.85rem' }}>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            if (onSelectTicket) {
                              onSelectTicket(ticket);
                            }
                          }}
                          onKeyDown={e => {
                            if ((e.key === 'Enter' || e.key === ' ') && onSelectTicket) {
                              onSelectTicket(ticket);
                            }
                          }}
                          className="fw-bold text-primary"
                          style={{ cursor: 'pointer' }}
                          title={`Abrir ticket #${ticket.idTicket}`}
                        >
                          #{ticket.idTicket}
                        </span>
                        {ticket.bIsCombinado && (
                          <span
                            className="badge ms-1"
                            title={ticket.idTicketDestino ? `Combinado con Ticket #${ticket.idTicketDestino}${ticket.vMotivoCombinacion ? ': ' + ticket.vMotivoCombinacion : ''}` : 'Ticket combinado'}
                            style={{
                              fontSize: '0.65rem',
                              backgroundColor: isDark ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff',
                              color: isDark ? '#d8b4fe' : '#7e22ce'
                            }}
                          >
                            Combinado{ticket.idTicketDestino ? ` → #${ticket.idTicketDestino}` : ''}
                          </span>
                        )}
                      </td>
                      <td
                        className="px-3 py-3"
                        style={{
                          fontSize: '0.82rem',
                          whiteSpace: 'nowrap',
                          color: isDark ? '#f8fafc' : '#1e293b'
                        }}
                      >
                        {createdDate}
                      </td>
                      <td className="px-3 py-3" style={{ fontSize: '0.85rem' }}>
                        <div className="fw-semibold" style={{ color: isDark ? '#f8fafc' : '#1e293b' }}>
                          {ticket.vCustomerName || 'Cliente sin nombre'}
                        </div>
                        {ticket.vTelefono && (
                          <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                            {ticket.vTelefono}
                          </small>
                        )}
                      </td>
                      <td className="px-3 py-3" style={{ fontSize: '0.82rem', maxWidth: '300px' }}>
                        <div className="fw-medium text-truncate" title={ticket.vAsunto}>
                          {ticket.vAsunto || 'Sin asunto'}
                        </div>
                        {ticket.vUltimoMensaje && (
                          <div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }} title={ticket.vUltimoMensaje}>
                            {formatWhatsAppText(ticket.vUltimoMensaje, { inlineOnly: true })}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3" style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {ticket.vNombreUsuarioAsignado ? (
                          <span className="d-inline-flex align-items-center gap-1">
                            <FontAwesomeIcon icon={faUserTie} style={{ fontSize: '0.75rem', opacity: 0.6 }} />
                            <span>{ticket.vNombreUsuarioAsignado}</span>
                          </span>
                        ) : (
                          <span className="text-muted fst-italic">Sin asignar</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center" style={{ whiteSpace: 'nowrap' }}>
                        <span
                          className="badge rounded-pill px-2 py-1"
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor: isDark ? badge.darkBg : badge.bg,
                            color: isDark ? badge.darkColor : badge.color
                          }}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center" style={{ whiteSpace: 'nowrap' }}>
                        {ticket.iTiempoRespuestaMinutos != null ? (
                          <span
                            className="badge px-2 py-1 rounded d-inline-flex align-items-center gap-1"
                            style={{
                              backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#dcfce7',
                              color: isDark ? '#34d399' : '#15803d',
                              fontSize: '0.74rem',
                              fontWeight: 600
                            }}
                            title="Tiempo que tardó el asesor en enviar el primer mensaje"
                          >
                            <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.70rem' }} />
                            <span>1er mensaje en {formatDurationMinutes(ticket.iTiempoRespuestaMinutos)}</span>
                          </span>
                        ) : (
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-end" style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 px-2 py-1"
                          style={{ fontSize: '0.78rem', borderRadius: '6px' }}
                          onClick={() => {
                            if (onSelectTicket) {
                              onSelectTicket(ticket);
                            }
                          }}
                          title={`Ver detalle del ticket #${ticket.idTicket}`}
                        >
                          <FontAwesomeIcon icon={faEye} />
                          <span>Ver</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Vista Móvil: Cards individuales rediseñadas con mayor espacio y holgura */}
          <div className="d-md-none p-3 p-sm-4 d-flex flex-column gap-3.5">
            {tickets.map(ticket => {
              const badge = getStatusBadge(ticket.iEstadoTicket);
              const createdDate = ticket.dCreatedDate
                ? new Date(ticket.dCreatedDate).toLocaleDateString('es-MX', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : '-';

              return (
                <div
                  key={ticket.idTicket}
                  onClick={() => onSelectTicket && onSelectTicket(ticket)}
                  className="rounded-3 position-relative"
                  style={{
                    backgroundColor: isDark ? '#111c2a' : '#ffffff',
                    border: isDark ? '1px solid #1e2c3d' : '1px solid #e2e8f0',
                    borderLeft: `4px solid ${badge.accentColor}`,
                    boxShadow: isDark
                      ? '0 4px 14px rgba(0, 0, 0, 0.3)'
                      : '0 2px 10px rgba(15, 23, 42, 0.06)',
                    padding: '1.15rem 1.15rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {/* 1. Encabezado: Avatar + Cliente + Estatus */}
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                    <div className="d-flex align-items-center gap-2.5 min-w-0">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold shadow-xs"
                        style={{
                          width: '42px',
                          height: '42px',
                          fontSize: '0.88rem',
                          backgroundColor: isDark ? badge.darkBg : badge.bg,
                          color: isDark ? badge.darkColor : badge.color,
                          border: isDark ? `1px solid ${badge.darkBg}` : '1px solid rgba(0,0,0,0.06)'
                        }}
                      >
                        {getInitials(ticket.vCustomerName || 'Cliente')}
                      </div>

                      <div className="d-flex flex-column min-w-0">
                        <span
                          className="fw-bold text-truncate"
                          style={{
                            fontSize: '0.94rem',
                            color: isDark ? '#f8fafc' : '#0f172a',
                            lineHeight: 1.3
                          }}
                        >
                          {ticket.vCustomerName || 'Cliente sin nombre'}
                        </span>
                        {ticket.vTelefono && (
                          <span
                            className="d-flex align-items-center gap-1.5 text-muted text-truncate mt-0.5"
                            style={{ fontSize: '0.76rem' }}
                          >
                            <FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: '0.70rem', opacity: 0.7 }} />
                            <span>{ticket.vTelefono}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="d-flex flex-column align-items-end flex-shrink-0 gap-1.5">
                      <span
                        className="badge rounded-pill px-2.5 py-1"
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          backgroundColor: isDark ? badge.darkBg : badge.bg,
                          color: isDark ? badge.darkColor : badge.color,
                          letterSpacing: '0.02em'
                        }}
                      >
                        {badge.label}
                      </span>
                    </div>
                  </div>

                  {/* 2. Barra de Metadatos: Ticket ID, Combinado y Fecha */}
                  <div
                    className="d-flex align-items-center justify-content-between gap-2 mb-3 pb-2.5 border-bottom flex-wrap"
                    style={{ borderColor: isDark ? '#1a2736' : '#f1f5f9' }}
                  >
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span
                        className="badge px-2.5 py-1"
                        style={{
                          fontSize: '0.74rem',
                          fontWeight: 700,
                          backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                          color: isDark ? '#38bdf8' : '#0284c7',
                          border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                          borderRadius: '6px'
                        }}
                      >
                        #TCK-{ticket.idTicket}
                      </span>

                      {ticket.bIsCombinado && (
                        <span
                          className="badge px-2 py-1"
                          title={
                            ticket.idTicketDestino
                              ? `Combinado con Ticket #${ticket.idTicketDestino}${
                                  ticket.vMotivoCombinacion ? ': ' + ticket.vMotivoCombinacion : ''
                                }`
                              : 'Ticket combinado'
                          }
                          style={{
                            fontSize: '0.66rem',
                            backgroundColor: isDark ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff',
                            color: isDark ? '#d8b4fe' : '#7e22ce',
                            borderRadius: '6px'
                          }}
                        >
                          Combinado{ticket.idTicketDestino ? ` → #${ticket.idTicketDestino}` : ''}
                        </span>
                      )}
                    </div>

                    <span
                      className="d-flex align-items-center gap-1.5 text-muted"
                      style={{ fontSize: '0.74rem' }}
                    >
                      <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: '0.70rem', opacity: 0.6 }} />
                      <span>{createdDate}</span>
                    </span>
                  </div>

                  {/* 3. Asunto y Último Mensaje con mayor holgura */}
                  {(ticket.vAsunto || ticket.vUltimoMensaje) && (
                    <div
                      className="p-3 rounded-2 mb-3"
                      style={{
                        backgroundColor: isDark ? 'rgba(11, 20, 31, 0.7)' : '#f8fafc',
                        border: isDark ? '1px solid #1a2736' : '1px solid #edf2f7'
                      }}
                    >
                      {ticket.vAsunto && (
                        <div
                          className="fw-semibold text-truncate mb-1.5"
                          style={{
                            fontSize: '0.82rem',
                            color: isDark ? '#38bdf8' : '#0284c7'
                          }}
                          title={ticket.vAsunto}
                        >
                          {ticket.vAsunto}
                        </div>
                      )}
                      {ticket.vUltimoMensaje && (
                        <div
                          className="text-truncate"
                          style={{
                            fontSize: '0.78rem',
                            color: isDark ? '#cbd5e1' : '#475569',
                            lineHeight: 1.45
                          }}
                          title={ticket.vUltimoMensaje}
                        >
                          {formatWhatsAppText(ticket.vUltimoMensaje, { inlineOnly: true })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 4. Pie del Card: Asesor, SLA de respuesta y Botón Ver con padding generoso */}
                  <div
                    className="d-flex align-items-center justify-content-between gap-2 pt-2.5 border-top flex-wrap"
                    style={{ borderColor: isDark ? '#1a2736' : '#f1f5f9' }}
                  >
                    <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '0.76rem' }}>
                      {ticket.vNombreUsuarioAsignado ? (
                        <span
                          className="badge px-2.5 py-1.5 rounded d-inline-flex align-items-center gap-1.5 text-truncate"
                          style={{
                            backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                            color: isDark ? '#cbd5e1' : '#475569',
                            fontWeight: 500,
                            maxWidth: '160px'
                          }}
                        >
                          <FontAwesomeIcon icon={faUserTie} style={{ fontSize: '0.70rem', opacity: 0.7 }} />
                          <span className="text-truncate">{ticket.vNombreUsuarioAsignado}</span>
                        </span>
                      ) : (
                        <span className="text-muted fst-italic" style={{ fontSize: '0.76rem' }}>
                          Sin asignar
                        </span>
                      )}

                      {ticket.iTiempoRespuestaMinutos != null && (
                        <span
                          className="badge px-2.5 py-1.5 rounded d-inline-flex align-items-center gap-1.5"
                          style={{
                            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#ecfdf5',
                            color: isDark ? '#34d399' : '#059669',
                            fontSize: '0.72rem',
                            fontWeight: 600
                          }}
                          title="Tiempo que tardó el asesor en enviar el primer mensaje"
                        >
                          <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.68rem' }} />
                          <span>1er msg: {formatDurationMinutes(ticket.iTiempoRespuestaMinutos)}</span>
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1.5 px-3 py-1.5 ms-auto"
                      style={{
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        borderRadius: '6px'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        if (onSelectTicket) {
                          onSelectTicket(ticket);
                        }
                      }}
                      title={`Ver detalle del ticket #${ticket.idTicket}`}
                    >
                      <span>Ver ticket</span>
                      <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '0.68rem' }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportTable;
